import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Container
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { MDXEditor, headingsPlugin, listsPlugin, quotePlugin, thematicBreakPlugin, markdownShortcutPlugin, linkPlugin, linkDialogPlugin, imagePlugin, tablePlugin, codeBlockPlugin, codeMirrorPlugin, diffSourcePlugin, frontmatterPlugin, toolbarPlugin, UndoRedo, BoldItalicUnderlineToggles, CodeToggle, CreateLink, InsertImage, InsertTable, InsertThematicBreak, ListsToggle, BlockTypeSelect, Separator } from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import '../styles/mdx-editor-overrides.css';
import apiService from '../services/ApiService';

/**
 * Wrapper component to provide hooks to class component
 */
function EditDocumentPageWrapper() {
  const params = useParams();
  const navigate = useNavigate();
  const isNew = !params.id;
  
  return <EditDocumentPage 
    id={params.id} 
    navigate={navigate} 
    isNew={isNew} 
  />;
}

/**
 * Edit document page component
 */
class EditDocumentPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      document: {
        id: props.id || '',
        Term: '',
        Definition: '',
        path: '/',
        tags: []
      },
      loading: !props.isNew,
      saving: false,
      error: null,
      newTag: '',
      paths: ['/', '/technical', '/processes', '/reference'],
      showMarkdownHelp: false
    };
    
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleTagDelete = this.handleTagDelete.bind(this);
    this.handleTagInputChange = this.handleTagInputChange.bind(this);
    this.handleTagAdd = this.handleTagAdd.bind(this);

    this.toggleMarkdownHelp = this.toggleMarkdownHelp.bind(this);
    this.handleDefinitionChange = this.handleDefinitionChange.bind(this);
  }

  /**
   * Fetch document data when component mounts (for editing)
   */
  componentDidMount() {
    if (!this.props.isNew) {
      this.fetchDocument();
    }
  }

  /**
   * Fetch document from API
   */
  async fetchDocument() {
    try {
      const response = await apiService.getDocumentById(this.props.id);
      this.setState({ 
        document: response.data,
        loading: false 
      });
    } catch (error) {
      this.setState({
        error: error.response?.status === 404
          ? 'Document not found'
          : 'Error loading document. Please try again later.',
        loading: false
      });
    }
  }

  /**
   * Handle form input changes
   * @param {Object} event - Input change event
   */
  handleInputChange(event) {
    const { name, value } = event.target;
    this.setState(prevState => ({
      document: {
        ...prevState.document,
        [name]: value
      }
    }));
  }

  /**
   * Handle tag input changes
   * @param {Object} event - Input change event
   */
  handleTagInputChange(event) {
    this.setState({ newTag: event.target.value });
  }

  /**
   * Add a new tag
   * @param {Object} event - Form submit event
   */
  handleTagAdd(event) {
    event.preventDefault();
    const { newTag, document } = this.state;
    
    if (!newTag.trim()) return;
    
    // Don't add duplicate tags
    if (document.tags.includes(newTag.trim())) {
      this.setState({ newTag: '' });
      return;
    }
    
    this.setState(prevState => ({
      document: {
        ...prevState.document,
        tags: [...prevState.document.tags, newTag.trim()]
      },
      newTag: ''
    }));
  }

  /**
   * Delete a tag
   * @param {Number} index - Tag index to delete
   */
  handleTagDelete(index) {
    this.setState(prevState => ({
      document: {
        ...prevState.document,
        tags: prevState.document.tags.filter((_, i) => i !== index)
      }
    }));
  }

  /**
   * Save document
   */
  async handleSave() {
    const { document } = this.state;
    
    if (!document.Term.trim()) {
      this.setState({ error: 'Term is required' });
      return;
    }
    
    this.setState({ saving: true, error: null });
    
    try {
      let response;
      
      if (this.props.isNew) {
        response = await apiService.createDocument(document);
      } else {
        response = await apiService.updateDocument(document.id, document);
      }
      
      // Check for correct response structure
      if (response && response.success && response.data) {
        // Navigate to the document page
        this.props.navigate(`/documents/${response.data.id}`);
      } else {
        console.error('Unexpected response format when saving document:', response);
        this.setState({
          error: 'Received unexpected response format from the server.',
          saving: false
        });
      }
    } catch (error) {
      console.error('Error saving document:', error);
      this.setState({
        error: 'Failed to save document. Please try again.',
        saving: false
      });
    }
  }

  /**
   * Cancel editing and navigate back
   */
  handleCancel() {
    if (this.props.isNew) {
      this.props.navigate('/');
    } else {
      this.props.navigate(`/documents/${this.props.id}`);
    }
  }
  
  /**
   * Handle definition change from MDXEditor
   * @param {String} value - New definition value
   */
  handleDefinitionChange(value) {
    this.setState(prevState => ({
      document: {
        ...prevState.document,
        Definition: value
      }
    }));
  }


  /**
   * Toggle markdown help dialog
   */
  toggleMarkdownHelp() {
    this.setState(prevState => ({
      showMarkdownHelp: !prevState.showMarkdownHelp
    }));
  }

  render() {
    const { document, loading, saving, error, newTag, paths, showMarkdownHelp } = this.state;
    const { isNew } = this.props;

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    // Markdown help content
    const markdownHelp = (
      <Paper sx={{ p: 3, mb: 3, mt: 2, maxHeight: '400px', overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>Markdown Syntax Guide</Typography>
        <Box component="div" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Headings</Typography>
          <Box component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            # Heading 1
            ## Heading 2
            ### Heading 3
          </Box>
        </Box>
        
        <Box component="div" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Formatting</Typography>
          <Box component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            **Bold text**
            *Italic text*
            ~~Strikethrough~~
          </Box>
        </Box>
        
        <Box component="div" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Lists</Typography>
          <Box component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            - Item 1
            - Item 2
              - Nested item
            
            1. Numbered item 1
            2. Numbered item 2
          </Box>
        </Box>
        
        <Box component="div" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Links and Images</Typography>
          <Box component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            [Link text](https://example.com)
            ![Alt text](image-url.jpg)
          </Box>
        </Box>
        
        <Box component="div" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Code</Typography>
          <Box component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            {`\`inline code\`

\`\`\`javascript
// Code block
function example() {
  return true;
}
\`\`\``}
          </Box>
        </Box>
        
        <Box component="div" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Blockquotes</Typography>
          <Box component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            {"> This is a blockquote\n> It can span multiple lines"}
          </Box>
        </Box>
        
        <Box component="div" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Tables</Typography>
          <Box component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            | Header 1 | Header 2 |
            | -------- | -------- |
            | Cell 1   | Cell 2   |
            | Cell 3   | Cell 4   |
          </Box>
        </Box>
      </Paper>
    );

    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{
              color: '#6c0c46',
              fontWeight: 600,
              fontSize: { xs: '1.75rem', sm: '2.125rem' }
            }}
          >
            {isNew ? 'Create New Document' : 'Edit Document'}
          </Typography>
          
          <Tooltip title="Markdown Reference">
            <IconButton 
              onClick={this.toggleMarkdownHelp} 
              sx={{
                color: '#6c0c46',
                '&:hover': {
                  backgroundColor: 'rgba(108, 12, 70, 0.1)'
                }
              }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {showMarkdownHelp && markdownHelp}

        <Paper 
          elevation={2}
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            borderRadius: 2,
            border: '1px solid rgba(108, 12, 70, 0.1)'
          }}
        >
          {/* Document form */}
          <Stack spacing={3}>
            <TextField
              name="Term"
              label="Term"
              value={document.Term || ''}
              onChange={this.handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6c0c46'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6c0c46'
                }
              }}
            />

            <FormControl fullWidth>
              <InputLabel 
                id="path-label"
                sx={{
                  '&.Mui-focused': {
                    color: '#6c0c46'
                  }
                }}
              >
                Document Path
              </InputLabel>
              <Select
                labelId="path-label"
                name="path"
                value={document.path}
                onChange={this.handleInputChange}
                label="Document Path"
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6c0c46'
                  }
                }}
              >
                {paths.map(path => (
                  <MenuItem key={path} value={path}>{path}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{ 
                  color: '#6c0c46',
                  fontWeight: 600
                }}
              >
                Tags
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 1 }}>
                {document.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => this.handleTagDelete(index)}
                    sx={{ 
                      mr: 1, 
                      mb: 1,
                      backgroundColor: 'rgba(108, 12, 70, 0.1)',
                      color: '#6c0c46',
                      '& .MuiChip-deleteIcon': {
                        color: '#6c0c46'
                      }
                    }}
                  />
                ))}
              </Box>
              
              <Box component="form" onSubmit={this.handleTagAdd} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Add tag"
                  value={newTag}
                  onChange={this.handleTagInputChange}
                  variant="outlined"
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#6c0c46'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#6c0c46'
                    }
                  }}
                />
                <Button 
                  type="submit" 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    minWidth: '80px',
                    borderColor: '#6c0c46',
                    color: '#6c0c46',
                    '&:hover': {
                      borderColor: '#5a0a3a',
                      backgroundColor: 'rgba(108, 12, 70, 0.1)'
                    }
                  }}
                >
                  Add
                </Button>
              </Box>
            </Box>

            {/* Enhanced Editor Section */}
            <Paper 
              elevation={1}
              sx={{ 
                borderRadius: 2,
                border: '1px solid rgba(108, 12, 70, 0.2)',
                overflow: 'hidden',
                '& .mdxeditor': { 
                  minHeight: '500px',
                  border: 'none',
                  '& *': {
                    boxSizing: 'border-box'
                  },
                  '& .mdxeditor-content-editable': {
                    '& ul, & ol': {
                      paddingInlineStart: '24px !important',
                      marginBlockStart: '1em',
                      marginBlockEnd: '1em'
                    },
                    '& li': {
                      display: 'list-item !important',
                      listStylePosition: 'outside !important'
                    }
                  },
                  '& .mdxeditor-toolbar': {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e0e0e0',
                    '& button': {
                      color: '#6c0c46',
                      '&:hover': {
                        backgroundColor: 'rgba(108, 12, 70, 0.08)'
                      },
                      '&[data-state="on"]': {
                        backgroundColor: '#6c0c46',
                        color: 'white'
                      }
                    },
                    '& .mdxeditor-select-trigger': {
                      color: '#6c0c46',
                      '&:hover': {
                        backgroundColor: 'rgba(108, 12, 70, 0.08)'
                      }
                    }
                  },
                  '& .mdxeditor-rich-text-editor': {
                    padding: '16px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    lineHeight: '1.6',
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: '#6c0c46',
                      marginTop: '1.5em',
                      marginBottom: '0.5em',
                      '&:first-child': {
                        marginTop: '0'
                      }
                    },
                    '& p': {
                      marginTop: '0',
                      marginBottom: '1em',
                      lineHeight: '1.6',
                      '&:last-child': {
                        marginBottom: '0'
                      }
                    },
                    '& a': {
                      color: '#3d7185'
                    },
                    '& code': {
                      backgroundColor: '#f5f5f5',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      fontSize: '0.9em'
                    },
                    '& pre': {
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px'
                    },
                    '& ul, & ol': {
                      paddingLeft: '24px !important',
                      marginLeft: '0 !important',
                      listStylePosition: 'outside !important'
                    },
                    '& ul': {
                      listStyleType: 'disc !important'
                    },
                    '& ol': {
                      listStyleType: 'decimal !important'
                    },
                    '& li': {
                      marginBottom: '4px',
                      paddingLeft: '4px',
                      '& ul, & ol': {
                        marginTop: '4px',
                        marginBottom: '4px',
                        paddingLeft: '20px !important'
                      }
                    },
                    '& ul ul': {
                      listStyleType: 'circle !important'
                    },
                    '& ul ul ul': {
                      listStyleType: 'square !important'
                    }
                  }
                }
              }}
            >
              <Box sx={{ p: 2 }}>
                <MDXEditor
                  markdown={document.Definition || ''}
                  onChange={this.handleDefinitionChange}
                  plugins={[
                    headingsPlugin(),
                    listsPlugin(),
                    quotePlugin(),
                    thematicBreakPlugin(),
                    markdownShortcutPlugin(),
                    linkPlugin(),
                    linkDialogPlugin(),
                    imagePlugin(),
                    tablePlugin(),
                    codeBlockPlugin({ defaultCodeBlockLanguage: 'javascript' }),
                    codeMirrorPlugin({ codeBlockLanguages: { js: 'JavaScript', css: 'CSS', txt: 'Text', tsx: 'TypeScript', ts: 'TypeScript' } }),
                    diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: '' }),
                    frontmatterPlugin(),
                    toolbarPlugin({
                      toolbarContents: () => (
                        <>
                          <UndoRedo />
                          <Separator />
                          <BoldItalicUnderlineToggles />
                          <CodeToggle />
                          <Separator />
                          <ListsToggle />
                          <Separator />
                          <BlockTypeSelect />
                          <Separator />
                          <CreateLink />
                          <InsertImage />
                          <Separator />
                          <InsertTable />
                          <InsertThematicBreak />
                        </>
                      )
                    })
                  ]}
                />
              </Box>
            </Paper>
          </Stack>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<CancelIcon />}
            onClick={this.handleCancel}
            sx={{
              borderColor: '#666',
              color: '#666',
              '&:hover': {
                borderColor: '#444',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={this.handleSave}
            disabled={saving}
            sx={{
              backgroundColor: '#6c0c46',
              '&:hover': {
                backgroundColor: '#5a0a3a'
              },
              '&:disabled': {
                backgroundColor: 'rgba(108, 12, 70, 0.5)'
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Document'}
          </Button>
        </Box>
      </Container>
    );
  }
}

export default EditDocumentPageWrapper;
